import { useCallback, useMemo, useState } from 'react';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';
import { H3 } from '@amzn/stencil-react-components/text';
import {
  Expander,
  ExpanderChevronPosition,
  ExpanderLayout,
} from '@amzn/stencil-react-components/expander';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
// standards-ignore: cross-feature — IconPlusSmall is a generic "add" affordance; a shared wrapper over a bare icon would add indirection without value
import { IconPlusSmall } from '@amzn/stencil-react-icons';

import { SubQuestionBasicFields } from './SubQuestionBasicFields';
import {
  TaskDetailsFields,
  type TaskDetailsValue,
} from '../../../shared/ui/forms/TaskDetailsFields';
import { Divider } from '../../../shared/ui/Divider';
import { useMessages } from '../../../shared/i18n/useMessages';
import { SubQuestionRequirementToggle } from './SubQuestionRequirementToggle';
import { SubQuestionDataCollectionFields } from './SubQuestionDataCollectionFields';
import { SubQuestionResponseTypePicker } from './SubQuestionResponseTypePicker';
import { SubQuestionRowHeader } from './SubQuestionRowHeader';
import { responseTypeLabelKey } from './responseTypeOptions';
import { SUB_QUESTIONS_BUNDLE, type SubKey } from './subQuestionsMessages';
import { isDataCollectionType } from '../../../shared/domain/responseTypeClassification';
import {
  DEFAULT_RESPONSE_TYPE,
  normalizeSubForResponseType,
} from '../../../shared/domain/subQuestionResponseType';
import { ShowOn, type ResponseType } from '../../../../types/helios/Question';
import { EMPTY_TASK_DETAILS, stripTask, toTaskDetails } from '../logic/subQuestionTask';
import type { SubQuestionFieldErrors } from '../../../shared/domain/subQuestionValidation';
import type { SubQuestion } from '../logic/reviewTypes';

/** All-false field errors — module-level so an omitted `fieldErrors` prop is
 *  reference-stable and a clean row shows no inline errors. */
const NO_FIELD_ERRORS: SubQuestionFieldErrors = {
  title: false,
  instruction: false,
  taskName: false,
  taskDescription: false,
  taskDuration: false,
};

/** Shared `justifyContent` value for the task-toggle row. */
const SPACE_BETWEEN = 'space-between';
/** Right-aligns the lone Add Task button when the Task Details heading is
 *  hidden (no task yet) so it matches the "+ Add Sub Question" placement. */
const FLEX_END = 'flex-end';

interface RowToggleConfig {
  readonly key: string;
  readonly label: string;
  readonly info: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly field: string;
}

export interface SubQuestionRowProps {
  readonly sub: SubQuestion;
  readonly index: number;
  readonly disabled: boolean;
  readonly onChange: (index: number, next: SubQuestion) => void;
  readonly onDelete: (index: number) => void;
  /** Whether the row starts expanded. Existing rows start collapsed (matches
   *  the mock); a just-added row is passed `true` so the author can fill it in. */
  readonly defaultExpanded?: boolean;
  /** Whether the parent question has conditional sub-questions enabled. When
   *  true, a data-collection sub shows the "Show when parent is" selector.
   *  (A Pass/Fail sub can never be conditional — the parent gate blocks
   *  enabling conditional while any P/F sub exists.) */
  readonly conditionalEnabled?: boolean;
  /** When true, the row header shows a single warning marker flagging ANY
   *  validation issue on this sub — a select type with fewer than two options,
   *  a Pass/Fail sub blocking conditional display, or (later) a required field.
   *  The specific message lives in the step/page banner; this is the per-row
   *  "fix this" signal. */
  readonly hasIssue?: boolean;
  /** Per-field inline-error flags for THIS sub (from `subQuestionFieldErrors`).
   *  Each field shows the native red error state when its flag is true; clears
   *  live as the field is filled. Defaults to all-false. */
  readonly fieldErrors?: SubQuestionFieldErrors;
  readonly dataTestId?: string;
}

/**
 * One collapsible sub-question editor row: title, rich-text instruction, the
 * four Pass/Fail requirement toggles, and an optional task (add/remove) via
 * the shared `TaskDetailsFields`. Controlled — patches the sub-question and
 * hands it up via `onChange(index, next)`.
 */
export function SubQuestionRow({
  sub,
  index,
  disabled,
  onChange,
  onDelete,
  defaultExpanded = false,
  conditionalEnabled = false,
  hasIssue = false,
  fieldErrors = NO_FIELD_ERRORS,
  dataTestId,
}: SubQuestionRowProps): JSX.Element {
  const { t, f, isLoading: isBundleLoading } = useMessages<SubKey>(SUB_QUESTIONS_BUNDLE);
  // Existing rows start collapsed (matches the mock); a just-added row is
  // seeded expanded via defaultExpanded. The header chevron toggles it after.
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const patch = useCallback(
    (fields: Partial<SubQuestion>) => onChange(index, { ...sub, ...fields }),
    [index, sub, onChange],
  );

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => patch({ title: e.target.value }),
    [patch],
  );
  const handleInstructionChange = useCallback(
    (html: string) => patch({ instruction: html }),
    [patch],
  );
  const handleCommentOnFail = useCallback(
    (checked: boolean) => patch({ requireCommentOnFail: checked }),
    [patch],
  );
  const handleEvidenceOnFail = useCallback(
    (checked: boolean) => patch({ requireEvidenceOnFail: checked }),
    [patch],
  );
  const handleCommentOnPass = useCallback(
    (checked: boolean) => patch({ requireCommentOnPass: checked }),
    [patch],
  );
  const handleEvidenceOnPass = useCallback(
    (checked: boolean) => patch({ requireEvidenceOnPass: checked }),
    [patch],
  );

  // Response type change: rebuild the sub via the pure normalizer (drops
  // fields that no longer apply — scored P/F flags ↔ unconditional flags,
  // options, conditional trigger) and emit the clean result. No inline
  // mutation; the whole next sub is handed up through onChange.
  const handleResponseTypeChange = useCallback(
    (value: string) =>
      onChange(index, normalizeSubForResponseType(sub, value as ResponseType)),
    [index, sub, onChange],
  );
  const handleShowOnChange = useCallback(
    (value: string) => patch({ showWhenParentIs: value as ShowOn }),
    [patch],
  );

  const responseType = sub.responseType ?? DEFAULT_RESPONSE_TYPE;
  const isDataCollection = isDataCollectionType(sub.responseType);

  const handleTaskChange = useCallback(
    (next: TaskDetailsValue) => patch({ taskDetails: toTaskDetails(next) }),
    [patch],
  );
  const handleAddTask = useCallback(
    () => patch({ taskDetails: EMPTY_TASK_DETAILS }),
    [patch],
  );
  const handleRemoveTask = useCallback(
    () => onChange(index, stripTask(sub)),
    [index, sub, onChange],
  );
  // In the header, stop propagation so the delete click doesn't also toggle
  // the surrounding expander trigger.
  const handleDeleteFromHeader = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete(index);
    },
    [index, onDelete],
  );

  const headerText = f('subRowLabel', {
    number: index + 1,
    title: sub.title.trim().length > 0 ? sub.title : t('subUntitledLabel'),
  });

  const hasTask = sub.taskDetails !== undefined;
  const taskValue: TaskDetailsValue = sub.taskDetails ?? EMPTY_TASK_DETAILS;
  // Map this sub's task field-error flags into the localized error MESSAGES
  // TaskDetailsFields renders (undefined = no error). Memoized so the child
  // prop stays reference-stable across renders.
  const taskErrors = useMemo(
    () => ({
      ...(fieldErrors.taskName ? { name: t('taskNameRequiredError') } : {}),
      ...(fieldErrors.taskDescription
        ? { description: t('taskDescriptionRequiredError') }
        : {}),
      ...(fieldErrors.taskDuration ? { duration: t('taskDurationRequiredError') } : {}),
    }),
    [fieldErrors, t],
  );

  const toggles: readonly RowToggleConfig[] = [
    {
      key: 'commentOnFail',
      label: t('subCommentOnFailLabel'),
      info: t('subCommentOnFailInfo'),
      checked: sub.requireCommentOnFail ?? false,
      onChange: handleCommentOnFail,
      field: 'require-comment-on-fail',
    },
    {
      key: 'evidenceOnFail',
      label: t('subEvidenceOnFailLabel'),
      info: t('subEvidenceOnFailInfo'),
      checked: sub.requireEvidenceOnFail ?? false,
      onChange: handleEvidenceOnFail,
      field: 'require-evidence-on-fail',
    },
    {
      key: 'commentOnPass',
      label: t('subCommentOnPassLabel'),
      info: t('subCommentOnPassInfo'),
      checked: sub.requireCommentOnPass ?? false,
      onChange: handleCommentOnPass,
      field: 'require-comment-on-pass',
    },
    {
      key: 'evidenceOnPass',
      label: t('subEvidenceOnPassLabel'),
      info: t('subEvidenceOnPassInfo'),
      checked: sub.requireEvidenceOnPass ?? false,
      onChange: handleEvidenceOnPass,
      field: 'require-evidence-on-pass',
    },
  ];
  // Two rows of two (Fail pair, Pass pair) for the 2×2 grid.
  const toggleRows: readonly (readonly RowToggleConfig[])[] = [
    toggles.slice(0, 2),
    toggles.slice(2, 4),
  ];
  const infoAltText = t('subInfoAltText');

  // Render the row header as: [title text] … [delete button], so delete sits
  // inline on the header instead of on its own line. The delete button stops
  // propagation so clicking it doesn't also toggle the expander.
  // Response-type badge label on the collapsed header so the author can scan
  // each sub's type without expanding.
  const typeBadgeLabel = t(responseTypeLabelKey(sub.responseType));

  const renderTitle = useCallback(
    ({ titleText, toggle }: { titleText?: string; toggle: () => void }): JSX.Element => (
      <SubQuestionRowHeader
        titleText={titleText}
        onToggle={toggle}
        typeBadgeLabel={typeBadgeLabel}
        hasIssue={hasIssue}
        issueLabel={t('subIssueMarkerLabel')}
        onDelete={handleDeleteFromHeader}
        deleteAltText={t('subDeleteAltText')}
        disabled={disabled}
        {...(dataTestId !== undefined ? { dataTestId } : {})}
      />
    ),
    [t, handleDeleteFromHeader, disabled, dataTestId, typeBadgeLabel, hasIssue],
  );

  return (
    <Expander
      titleText={headerText}
      layout={ExpanderLayout.Card}
      chevronPosition={ExpanderChevronPosition.Leading}
      isExpanded={isExpanded}
      onToggle={setIsExpanded}
      renderTitle={renderTitle}
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      <Col gridGap="dimensions.spacing.300" width="100%">
        <SubQuestionBasicFields
          orderIndex={sub.orderIndex}
          title={sub.title}
          instruction={sub.instruction}
          onTitleChange={handleTitleChange}
          onInstructionChange={handleInstructionChange}
          titleError={fieldErrors.title}
          instructionError={fieldErrors.instruction}
          disabled={disabled}
          isBundleLoading={isBundleLoading}
          {...(dataTestId !== undefined ? { dataTestId } : {})}
        />

        {/* Response type (+ conditional trigger). Defaults to Pass/Fail
         *  (agent seed); changing it rebuilds the sub via the normalizer so
         *  stale per-type fields never ride along. */}
        <SubQuestionResponseTypePicker
          responseType={responseType}
          onResponseTypeChange={handleResponseTypeChange}
          conditionalEnabled={conditionalEnabled}
          excludePassFail={conditionalEnabled}
          {...(sub.showWhenParentIs !== undefined
            ? { showWhenParentIs: sub.showWhenParentIs }
            : {})}
          onShowOnChange={handleShowOnChange}
          disabled={disabled}
          {...(dataTestId !== undefined ? { dataTestId } : {})}
        />

        {/* Answer-config body: data-collection subs get the options editor +
         *  unconditional requirement toggles; scored P/F subs keep the four
         *  on-fail / on-pass toggles. */}
        {isDataCollection ? (
          <SubQuestionDataCollectionFields
            sub={sub}
            responseType={responseType}
            disabled={disabled}
            patch={patch}
            {...(dataTestId !== undefined ? { dataTestId } : {})}
          />
        ) : (
          // 2×2 grid (Comment | Attachment on Fail, then on Pass), matching the
          // Question Configuration step's RequirementTogglesGrid.
          toggleRows.map((toggleRow) => (
            <Row
              key={toggleRow.map((toggle) => toggle.key).join('-')}
              gridGap="dimensions.spacing.400"
              flexWrap="wrap"
            >
              {toggleRow.map((toggle) => (
                <View key={toggle.key} flex={1} minWidth={0}>
                  <SubQuestionRequirementToggle
                    label={toggle.label}
                    info={toggle.info}
                    infoAltText={infoAltText}
                    checked={toggle.checked}
                    onChange={toggle.onChange}
                    disabled={disabled}
                    trackingId={`audit-whiz.review.sub-q.${sub.orderIndex}.${toggle.field}`}
                    {...(dataTestId !== undefined
                      ? { dataTestId: `${dataTestId}-${toggle.key}` }
                      : {})}
                  />
                </View>
              ))}
            </Row>
          ))
        )}

        <Divider />

        <Row
          alignItems="center"
          justifyContent={hasTask ? SPACE_BETWEEN : FLEX_END}
          flexWrap="wrap"
        >
          {hasTask ? <H3>{t('subTaskHeading')}</H3> : null}
          <Button
            variant={ButtonVariant.Secondary}
            onClick={hasTask ? handleRemoveTask : handleAddTask}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-task-toggle` : undefined}
            {...(hasTask ? {} : { icon: <IconPlusSmall aria-hidden="true" /> })}
          >
            {hasTask ? t('subRemoveTaskButton') : t('subAddTaskButton')}
          </Button>
        </Row>

        {hasTask && !isBundleLoading && (
          <TaskDetailsFields
            value={taskValue}
            onChange={handleTaskChange}
            disabled={disabled}
            errors={taskErrors}
            trackingIdPrefix={`audit-whiz.review.sub-q.${sub.orderIndex}`}
            labels={{
              nameLabel: t('criteriaTaskNameLabel'),
              namePlaceholder: t('criteriaTaskNamePlaceholder'),
              descriptionLabel: t('criteriaTaskDescriptionLabel'),
              descriptionPlaceholder: t('criteriaTaskDescriptionPlaceholder'),
              durationLabel: t('criteriaTaskDurationLabel'),
              durationPlaceholder: t('criteriaTaskDurationPlaceholder'),
            }}
            {...(dataTestId !== undefined ? { dataTestId: `${dataTestId}-task` } : {})}
          />
        )}
      </Col>
    </Expander>
  );
}
